// src/components/PhotoAlbums.js
import React, { useState, useEffect, useContext } from "react";
import FamilyHomeTabs from "./FamilyHomeTabs";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import axios from "axios";
import { useParams } from "react-router-dom";
import { AuthContext } from "react-oauth2-code-pkce";
import { motion } from "framer-motion";
import { Images } from "lucide-react";
import toast from "react-hot-toast";
import imageCompression from "browser-image-compression";

//const API_BASE_URL = "http://localhost:8129/api-family";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MAX_PHOTOS_PER_ALBUM = 20; // adjust as needed


//
// Album Modal
//
const AlbumModal = ({ mode, album, familyId, onSave, onClose, token }) => {
  const [name, setName] = useState(album?.name || "");
  const [cover, setCover] = useState(album?.imgUrl || album?.cover || null);
  const [coverFile, setCoverFile] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const colors = [
    "bg-purple-100",
    "bg-blue-100",
    "bg-pink-100",
    "bg-green-100",
  ];

  const handleCoverChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    // ✅ Check file size
    if (selected.size > 1 * 1024 * 1024) {
      setPendingFile(selected);
      setShowConfirm(true);
      return;
    }

    // ✅ Normal path (small file)
    setCover(URL.createObjectURL(selected));
    setCoverFile(selected);
  };

  const getDefaultImageFile = async () => {
    const response = await fetch("/images/default-album-cover.jpg");
    const blob = await response.blob();

    return new File([blob], "default-album.jpg", {
      type: blob.type,
    });
  };

  const onConfirm = async () => {
    if (!pendingFile) return;
    try {
      setCompressing(true); // ⏳ Start blocking UI
      const compressed = await imageCompression(pendingFile, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      });
      setCover(URL.createObjectURL(compressed));
      setCoverFile(compressed);
      toast.success(
        `Compressed ${pendingFile.name} → ${(compressed.size / 1024 / 1024).toFixed(2)} MB`,
      );
    } catch (err) {
      console.error("Compression failed:", err);
      setCover(URL.createObjectURL(pendingFile));
      setCoverFile(pendingFile);
    } finally {
      setPendingFile(null);
      setShowConfirm(false);
      setCompressing(false); // ✅ Unblock UI
    }
  };

  const onCancel = () => {
    // ❌ Don’t upload anything
    setPendingFile(null);
    setShowConfirm(false);
    toast("Upload cancelled");
  };

  const ConfirmDialog = ({ show, fileName, onConfirm, onCancel }) => {
    if (!show) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-sm text-center"
        >
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            Large File Detected
          </h2>
          <p className="text-gray-600 text-sm mb-5">
            <strong>{fileName}</strong> is larger than 1 MB. Would you like to
            compress it before uploading?
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
            >
              ❌ Cancel Upload
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-md"
            >
              Compress
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

  const handleSubmit = async () => {
    if (uploading) return; // 🚫 block duplicate submits

    if (!name) {
      toast.error("Album name is required.");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append(
        "familyAlbum",
        new Blob([JSON.stringify({ id: album?.id, name, familyId })], {
          type: "application/json",
        }),
      );
      // 📌 ADD MODE → send default image if none selected
      if (mode === "add") {
        if (coverFile) {
          formData.append("file", coverFile);
        } else {
          const defaultFile = await getDefaultImageFile();
          formData.append("file", defaultFile);
        }
      }

      // 📌 EDIT MODE → send file ONLY if user selected a new one
      if (mode === "edit" && coverFile) {
        formData.append("file", coverFile);
      }

      const { data } = await axios.post(
        `${API_BASE_URL}/family/album`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      onSave(data);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setUploading(false); // ✅ always reset
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden p-6 relative"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold"
        >
          &times;
        </button>

        <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
          {mode === "edit" ? "Edit Album" : "Create New Album"}
        </h2>

        {/* Album Name */}
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Album Name"
          className="w-full border border-gray-300 rounded-xl px-4 py-3 mb-4 focus:ring-2 focus:ring-purple-400 focus:outline-none transition"
        />

        {/* Cover Upload */}
        <input
          type="file"
          id="coverInput"
          accept="image/*"
          onChange={handleCoverChange}
          className="hidden"
        />
        <label
          htmlFor="coverInput"
          className="w-full flex justify-center items-center gap-2 px-4 py-2 rounded-xl bg-purple-50 text-purple-700 font-medium cursor-pointer border border-purple-200 hover:bg-purple-100 transition"
        >
          📁 Browse Cover
        </label>

        {/* Preview */}
        {cover ? (
          <img
            src={cover}
            alt="Album Cover"
            className="w-full h-44 object-cover mt-4 rounded-2xl shadow-md"
          />
        ) : (
          <div className="w-full h-44 mt-4 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-2xl bg-gray-50 text-gray-400 text-sm">
            📸 No cover selected
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-100 transition font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploading || compressing}
            className={`px-5 py-2 rounded-xl font-semibold transition
                ${
                  uploading || compressing
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:shadow-xl"
                }
              `}
          >
            {uploading
              ? "Uploading..."
              : mode === "edit"
                ? "Save Changes"
                : "Create Album"}
          </button>
        </div>
      </motion.div>
      <ConfirmDialog
        show={showConfirm}
        fileName={pendingFile?.name}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
      {compressing && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center z-[100]">
          <div className="bg-white px-6 py-4 rounded-2xl shadow-lg text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-purple-600 border-solid mx-auto mb-3"></div>
            <p className="text-gray-700 font-medium">
              Compressing image, please wait...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

//
// Media Modal
//
const MediaModal = ({ albumId, onSave, onClose, token, albumCurrentPhotosCount = 0 }) => {
  const [type, setType] = useState("image");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFilesChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    const currentPhotosCount =
      (albumCurrentPhotosCount || 0) + previews.length; // existing photos in album + current previews

    if (currentPhotosCount + selectedFiles.length > MAX_PHOTOS_PER_ALBUM) {
      toast.error(
        `You can upload a maximum of ${MAX_PHOTOS_PER_ALBUM} photos per album. You currently have ${albumCurrentPhotosCount || 0}.`
      );
      return;
    }

    const processedFiles = [];
    setCompressing(true); // show overlay if large files

    try {
      for (const file of selectedFiles) {
        if (file.size > 1 * 1024 * 1024) {
          // compress large files
          try {
            const compressed = await imageCompression(file, {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            });
            processedFiles.push(compressed);
            toast.success(
              `Compressed ${file.name} → ${(compressed.size / 1024 / 1024).toFixed(
                2
              )} MB`
            );
          } catch (err) {
            console.error("Compression failed for", file.name, err);
            processedFiles.push(file);
          }
        } else {
          processedFiles.push(file);
        }
      }

      setFiles(processedFiles);
      setPreviews(processedFiles.map((f) => URL.createObjectURL(f)));
    } finally {
      setCompressing(false);
    }
  };

  const onConfirm = async () => {
    if (!pendingFile) return;
    try {
      setCompressing(true); // ⏳ Start blocking UI
      const compressed = await imageCompression(pendingFile, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      });
      setFiles((prev) => [...prev, compressed]);
      setPreviews((prev) => [...prev, URL.createObjectURL(compressed)]);
      toast.success(
        `Compressed ${pendingFile.name} → ${(compressed.size / 1024 / 1024).toFixed(2)} MB`,
      );
    } catch (err) {
      console.error("Compression failed:", err);
      setFiles((prev) => [...prev, pendingFile]); // fallback
    } finally {
      setPendingFile(null);
      setShowConfirm(false);
      setCompressing(false); // ✅ Unblock UI
    }
  };

  /*   const onCancel = () => {
    if (pendingFile) {
      setFiles((prev) => [...prev, pendingFile]);
      setPreviews((prev) => [...prev, URL.createObjectURL(pendingFile)]);
    }
    setPendingFile(null);
    setShowConfirm(false);
  }; */
  const onCancel = () => {
    // ❌ Don’t add the image — just close the dialog
    setPendingFile(null);
    setShowConfirm(false);
    //toast("Upload cancelled");
  };

  const ConfirmDialog = ({ show, fileName, onConfirm, onCancel }) => {
    if (!show) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-sm text-center"
        >
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            Large File Detected
          </h2>
          <p className="text-gray-600 text-sm mb-5">
            <strong>{fileName}</strong> is larger than 1 MB. Would you like to
            compress it before uploading?
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
            >
              ❌ Cancel Upload
            </button>

            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-md"
            >
              Compress
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

  const handleSubmit = async () => {
    if (type === "image" && files.length === 0) {
      toast.error("Please upload at least one image.");
      return;
    }
    if (type === "video" && !videoUrl) {
      toast.error("Please enter a YouTube link.");
      return;
    }

    const noVideo = !videoUrl || videoUrl.trim() === "";
    const noImages = !files || files.length === 0;
    try {
      setUploading(true);
      let newItems = [];

      const formData = new FormData();

      // 👇 send DTO as JSON blob
      formData.append(
        "familyMediaDto",
        new Blob(
          [
            JSON.stringify({
              familyAlbumId: albumId,
              videoUrl: noVideo ? null : videoUrl.trim(),
            }),
          ],
          { type: "application/json" },
        ),
      );
      // 👇 Always attach files field (may be empty)
      if (!noImages) {
        files.forEach((file) => formData.append("files", file));
      } else {
        // 👇 explicitly send null instead of a fake file
        //formData.append("files", new Blob([JSON.stringify(null)], { type: "application/json" }));
      }

      const { data } = await axios.post(
        `${API_BASE_URL}/family/album/media`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      console.log(data);
      newItems = data.familyMediaResponseDtoList;
      onSave(albumId, newItems);
      onClose();
    } catch (err) {
      console.error("Error uploading media", err);
      toast.error("Failed to upload media");
    } finally {
      setUploading(false); // ✅ always reset
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Add Media</h2>

        <div className="flex mb-4 border-b">
          <button
            onClick={() => setType("image")}
            className={`flex-1 py-2 text-center font-medium transition ${
              type === "image"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-500 hover:text-blue-500"
            }`}
          >
            📷 Photos
          </button>
          <button
            onClick={() => setType("video")}
            className={`flex-1 py-2 text-center font-medium transition ${
              type === "video"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-500 hover:text-blue-500"
            }`}
          >
            🎥 Video
          </button>
        </div>

        {type === "image" ? (
          <div>
            <input
              type="file"
              id="fileInput"
              accept="image/*"
              multiple
              onChange={handleFilesChange}
              className="hidden"
            />
            <label
              htmlFor="fileInput"
              className="inline-flex items-center px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-sm font-medium cursor-pointer border"
            >
              Browse
            </label>

            {previews.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {previews.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt="Preview"
                    className="w-full h-24 object-cover rounded-md border"
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <input
            type="text"
            placeholder="YouTube Video URL"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            className="w-full border rounded px-3 py-2 mb-3"
          />
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 border rounded-md">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploading || compressing}
            className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? "Uploading..." : "Add"}
          </button>
        </div>
        {/* ✅ Add confirmation dialog here */}
        <ConfirmDialog
          show={showConfirm}
          fileName={pendingFile?.name}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
        {compressing && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center z-[100]">
            <div className="bg-white px-6 py-4 rounded-2xl shadow-lg text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-purple-600 border-solid mx-auto mb-3"></div>
              <p className="text-gray-700 font-medium">
                Compressing image, please wait...
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

//
// Main Component
//
const PhotoAlbums = () => {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [currentAlbum, setCurrentAlbum] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [activeAlbumId, setActiveAlbumId] = useState(null);
  const [showMyAlbums, setShowMyAlbums] = useState(false);
  const [filterMode, setFilterMode] = useState("all"); // "all" | "my"
  const [yearFilter, setYearFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [activeAlbumForGrid, setActiveAlbumForGrid] = useState(null); // NEW
  const [selectedAlbumIndex, setSelectedAlbumIndex] = useState(0);

  const [confirmDelete, setConfirmDelete] = useState({
    show: false,
    type: null, // "album" | "media"
    albumId: null,
    mediaId: null,
  });

  const getAlbumYears = () => {
    const years = albums
      .map((a) => new Date(a.createdOn).getFullYear())
      .filter((y) => !isNaN(y));

    const uniqueYears = [...new Set(years)].sort((a, b) => b - a);

    if (uniqueYears.length <= 5) {
      return uniqueYears.map((y) => ({ label: y.toString(), value: y }));
    }

    // group into ranges of 3 years
    const minYear = Math.min(...uniqueYears);
    const maxYear = Math.max(...uniqueYears);
    const ranges = [];
    let start = minYear;
    while (start <= maxYear) {
      let end = Math.min(start + 2, maxYear);
      ranges.push({ label: `${start}-${end}`, value: [start, end] });
      start = end + 1;
    }
    return ranges.reverse(); // show latest first
  };

  const filteredAlbums = albums.filter((album) => {
    // ✅ My Albums filter based on owner flag
    if (filterMode === "my" && !album.owner) return false;

    // ✅ Year filter
    if (yearFilter) {
      const albumYear = new Date(album.createdOn).getFullYear();
      if (Array.isArray(yearFilter)) {
        return albumYear >= yearFilter[0] && albumYear <= yearFilter[1];
      } else {
        return albumYear === yearFilter;
      }
    }
    return true;
  });

  const { id: familyId } = useParams();
  const { token } = useContext(AuthContext); // ✅ get token

  useEffect(() => {
    if (familyId && token) fetchAlbums(familyId);
  }, [familyId, token]);
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        if (selectedAlbum) {
          setSelectedAlbum(null); // close fullscreen viewer (images & YouTube)
        } else if (showMediaModal) {
          setShowMediaModal(false); // close MediaModal
        } else if (showAlbumModal) {
          setShowAlbumModal(false); // close AlbumModal
        }
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [selectedAlbum, showMediaModal, showAlbumModal]);

  const fetchAlbums = async (familyId) => {
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/family/album/${familyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAlbums(data);
    } catch (err) {
      console.error("Error fetching albums", err);
    }
  };

  const handleDeleteMedia = async (albumId, mediaId) => {
    try {
      await axios.delete(`${API_BASE_URL}/family/album/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // ✅ update albums
      const updatedAlbums = albums.map((a) =>
        a.id === albumId
          ? {
              ...a,
              familyMediaResponseDtoList: a.familyMediaResponseDtoList.filter(
                (m) => m.id !== mediaId,
              ),
            }
          : a,
      );
      setAlbums(updatedAlbums);

      // ✅ also update selectedAlbum if currently open
      if (selectedAlbum?.id === albumId) {
        const updatedAlbum = updatedAlbums.find((a) => a.id === albumId);
        setSelectedAlbum(updatedAlbum);
      }
    } catch (err) {
      console.error("Error deleting media", err);
    }
  };

  const handleSaveAlbum = (savedAlbum) => {
    setAlbums((prevAlbums) => {
      const exists = prevAlbums.some((a) => a.id === savedAlbum.id);
      if (exists) {
        return prevAlbums.map((a) => (a.id === savedAlbum.id ? savedAlbum : a));
      } else {
        return [savedAlbum, ...prevAlbums];
      }
    });
  };

  const requestDeleteAlbum = (albumId) => {
    setConfirmDelete({
      show: true,
      type: "album",
      albumId,
      mediaId: null,
    });
  };

  const requestDeleteMedia = (albumId, mediaId) => {
    setConfirmDelete({
      show: true,
      type: "media",
      albumId,
      mediaId,
    });
  };

  const handleDeleteAlbum = async (albumId) => {
    try {
      await axios.delete(`${API_BASE_URL}/family/album/${albumId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlbums(albums.filter((a) => a.id !== albumId));
    } catch (err) {
      console.error("Error deleting album", err);
    }
  };
  const extractYouTubeId = (url) => {
    if (!url) return null;

    // Match watch?v=, shorts/, or youtu.be/
    const reg =
      /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(reg);
    return match ? match[1] : null;
  };
  const getYouTubeThumbnail = (url) => {
    if (!url) return "/images/default-video-thumb.jpg"; // fallback
    const reg =
      /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^?&#/]+)/;
    const match = url.match(reg);
    if (!match) return "/images/default-video-thumb.jpg";
    return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
  };

  const handleAddMedia = (albumId, newItems) => {
    setAlbums(
      albums.map((a) =>
        a.id === albumId
          ? {
              ...a,
              familyMediaResponseDtoList: [
                ...(a.familyMediaResponseDtoList || []),
                ...newItems,
              ],
            }
          : a,
      ),
    );
  };

  const confirmDeleteAction = async () => {
    const { type, albumId, mediaId } = confirmDelete;

    try {
      if (type === "album") {
        await axios.delete(`${API_BASE_URL}/family/album/${albumId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAlbums((prev) => prev.filter((a) => a.id !== albumId));
      }

      if (type === "media") {
        await axios.delete(`${API_BASE_URL}/family/album/media/${mediaId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // ✅ Update albums list
        setAlbums((prev) =>
          prev.map((a) =>
            a.id === albumId
              ? {
                  ...a,
                  familyMediaResponseDtoList:
                    a.familyMediaResponseDtoList.filter(
                      (m) => m.id !== mediaId,
                    ),
                }
              : a,
          ),
        );

        // ✅ IMPORTANT: Update selectedAlbum (ImageGallery source)
        setSelectedAlbum((prev) => {
          if (!prev || prev.id !== albumId) return prev;

          const remaining = prev.familyMediaResponseDtoList.filter(
            (m) => m.id !== mediaId,
          );

          return remaining.length === 0
            ? null
            : { ...prev, familyMediaResponseDtoList: remaining };
        });
        // ✅ Update activeAlbumForGrid if open
        setActiveAlbumForGrid((prev) => {
          if (!prev || prev.id !== albumId) return prev;
          const remaining = prev.familyMediaResponseDtoList.filter((m) => m.id !== mediaId);
          return { ...prev, familyMediaResponseDtoList: remaining };
        });
      }
    } catch (err) {
      toast.error("Delete failed");
    } finally {
      setConfirmDelete({
        show: false,
        type: null,
        albumId: null,
        mediaId: null,
      });
    }
  };

  const getGalleryItems = (albumItems, album) =>
    albumItems.map((m) =>
      m.type === "video"
        ? {
            original: m.thumbnailUrl,
            thumbnail: getYouTubeThumbnail(m.videoUrl),
            renderItem: () => {
              const videoId = extractYouTubeId(m.videoUrl);
              const embedUrl = `https://www.youtube.com/embed/${videoId}`;
              return (
                <div className="relative video-wrapper flex items-center justify-center">
                  {album.owner && (
                    <button
                      onClick={() => requestDeleteMedia(album.id, m.id)}
                      className="absolute bottom-2 right-2 bg-red-600 text-white px-2 py-1 rounded text-xs"
                    >
                      🗑 Delete
                    </button>
                  )}
                  <iframe
                    key={m.id}
                    src={embedUrl}
                    title="YouTube video"
                    className="w-full h-[70vh] rounded-lg"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              );
            },
          }
        : {
            original: m.imgUrl || m.url,
            thumbnail:
              !m.thumbnailUrl || m.thumbnailUrl.endsWith("null")
                ? m.imgUrl
                : m.thumbnailUrl,
            renderItem: () => (
              <div className="relative">
                <img
                  src={m.imgUrl || m.url}
                  alt="Album media"
                  className="w-full h-[70vh] object-contain rounded-lg"
                />
              </div>
            ),
          },
    );

  return (
    <>
      <div className="bg-background px-8 py-2">
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-8">
            <div className="flex items-center gap-3 mb-4 sm:mb-0">
              <div className="p-3 bg-purple-100 rounded-2xl">
                <Images className="text-purple-700 w-6 h-6" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800">
                Family Albums
              </h1>
            </div>

            {/* Search */}
          </div>
          {!activeAlbumForGrid ? (
            <>
              <div className="flex flex-col items-center mb-1 gap-1 ">
                {/* 🎛️ Filter + Add Album Section */}
                <div className="w-full flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 gap-1 max-w-7xl mx-auto">
                  {/* 📱 Scrollable Filters (centered on mobile) */}
                  <div className="flex flex-nowrap sm:flex-wrap justify-start sm:justify-center items-center gap-2 p-4 sm:px-0">
                    {/* All Albums */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setFilterMode("all");
                        setYearFilter(null);
                      }}
                      className={`px-4 py-1.5 flex-shrink-0 text-sm font-medium rounded-full border transition-all duration-300 shadow-sm
                       ${
                         filterMode === "all" && !yearFilter
                           ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-500 shadow-md"
                           : "bg-white text-gray-700 border-gray-200 hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700"
                       }`}
                    >
                      All Albums
                    </motion.button>

                    {/* My Albums */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setFilterMode("my");
                        setYearFilter(null);
                      }}
                      className={`px-4 py-1.5 flex-shrink-0 text-sm font-medium rounded-full border transition-all duration-300 shadow-sm
                       ${
                         filterMode === "my" && !yearFilter
                           ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-500 shadow-md"
                           : "bg-white text-gray-700 border-gray-200 hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700"
                       }`}
                    >
                      My Albums
                    </motion.button>

                    {/* 📆 Year Filters */}
                    {getAlbumYears().map((y) => (
                      <motion.button
                        key={y.label}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() =>
                          setYearFilter((prev) =>
                            prev && prev.toString() === y.value.toString()
                              ? null
                              : y.value,
                          )
                        }
                        className={`px-4 py-1.5 flex-shrink-0 text-sm font-medium rounded-full border transition-all duration-300 shadow-sm
                         ${
                           yearFilter?.toString() === y.value.toString()
                             ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-500 shadow-md"
                             : "bg-white text-gray-700 border-gray-200 hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700"
                         }`}
                      >
                        {y.label}
                      </motion.button>
                    ))}
                  </div>

                  {/* ➕ Add Album Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setModalMode("add");
                      setCurrentAlbum(null);
                      setShowAlbumModal(true);
                    }}
                    className="flex items-center justify-center gap-2 text-sm font-semibold bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all duration-300 w-full sm:w-auto"
                  >
                    + Add Album
                  </motion.button>
                </div>
              </div>
              {/* 🎨 Album Grid Card */}

              {/* Album Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filteredAlbums
                  .slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage,
                  )
                  .map((album) => (
                    <motion.div
                      key={album.id}
                      whileHover={{ y: -6, scale: 1.02 }}
                      transition={{ duration: 0.3 }}
                      onClick={() => setActiveAlbumForGrid(album)}
                      className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border border-gray-100"
                    >
                      {/* Album Cover */}
                      <div className="relative w-full h-36 sm:h-40 md:h-44 lg:h-48 overflow-hidden rounded-t-2xl">
                        <img
                          src={album.imgUrl || album.cover}
                          alt={album.name}
                          className="w-full h-full object-cover transform hover:scale-105 transition duration-300"
                        />
                      </div>

                      {/* Album Info */}
                      <div className="px-8 py-2">
                        <h2 className="font-semibold text-base sm:text-lg text-gray-800 truncate">
                          {album.name}
                        </h2>
                        <p className="text-gray-500 text-xs sm:text-sm truncate">
                          by {album.createdBy} •{" "}
                          {album.familyMediaResponseDtoList?.length || 0} items
                        </p>
                      </div>

                      {/* Actions (Edit, Add Media, Delete) */}
                      {album.owner && (
                        <div className="flex justify-end gap-2 px-8 py-2 border-t border-gray-100">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setModalMode("edit");
                              setCurrentAlbum(album);
                              setShowAlbumModal(true);
                            }}
                            className="p-1 text-gray-600 hover:text-purple-600 hover:bg-gray-100 rounded transition text-sm"
                            title="Edit Album"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveAlbumId(album.id);
                              setShowMediaModal(true);
                            }}
                            className="p-1 text-gray-600 hover:text-green-600 hover:bg-gray-100 rounded transition text-sm"
                            title="Add Media"
                          >
                            ➕
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDeleteAlbum(album.id);
                            }}
                            className="p-1 text-gray-600 hover:text-red-600"
                          >
                            🗑️
                          </button>
                        </div>
                      )}
                    </motion.div>
                  ))}
              </div>

              {/* Pagination */}
              {filteredAlbums.length > itemsPerPage && (
                <div className="flex flex-wrap justify-center items-center gap-2 mt-6">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded-md bg-white disabled:opacity-50 hover:bg-gray-100 transition"
                  >
                    Prev
                  </button>

                  {Array.from(
                    { length: Math.ceil(filteredAlbums.length / itemsPerPage) },
                    (_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`px-3 py-1 border rounded-md ${
                          currentPage === i + 1
                            ? "bg-gray-300"
                            : "bg-white hover:bg-gray-100"
                        } transition`}
                      >
                        {i + 1}
                      </button>
                    ),
                  )}

                  <button
                    onClick={() =>
                      setCurrentPage((p) =>
                        Math.min(
                          p + 1,
                          Math.ceil(filteredAlbums.length / itemsPerPage),
                        ),
                      )
                    }
                    disabled={
                      currentPage ===
                      Math.ceil(filteredAlbums.length / itemsPerPage)
                    }
                    className="px-3 py-1 border rounded-md bg-white disabled:opacity-50 hover:bg-gray-100 transition"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Media Grid */}
              <div className="mt-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveAlbumForGrid(null)}
                  className="mb-4 px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-300"
                >
                  ← Back to Albums
                </motion.button>
                <h2 className="text-xl font-semibold mb-4">
                  {activeAlbumForGrid.name}
                </h2>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {(activeAlbumForGrid.familyMediaResponseDtoList || []).map(
                    (m) => (
                      <div key={m.id} className="relative cursor-pointer">
                        <img
                          src={
                            m.type === "video"
                              ? getYouTubeThumbnail(m.videoUrl)
                              : m.imgUrl || m.thumbnailUrl
                          }
                          alt=""
                          className="w-full h-32 object-cover rounded-md"
                          onClick={() => {
                            setSelectedAlbum(activeAlbumForGrid); // open fullscreen
                            const index = (
                              activeAlbumForGrid.familyMediaResponseDtoList ||
                              []
                            ).findIndex((media) => media.id === m.id);
                            setSelectedAlbumIndex(index >= 0 ? index : 0);
                          }}
                        />

                        {activeAlbumForGrid.owner && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDeleteMedia(activeAlbumForGrid.id, m.id);
                            }}
                            className="absolute top-1 right-1 bg-red-600 text-white rounded px-1 text-xs"
                          >
                            🗑 Delete
                          </button>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Album Viewer */}
        {selectedAlbum && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50"
            onClick={() => setSelectedAlbum(null)} // 👈 close when clicking background
          >
            {" "}
            <button
              onClick={() => setSelectedAlbum(null)}
              className="absolute top-4 right-4 text-white text-3xl"
            >
              &times;
            </button>
            <div
              className="w-full max-w-5xl p-4"
              onClick={(e) => e.stopPropagation()} // 👈 stop background close when clicking inside
            >
              <h2 className="text-center text-white text-xl font-semibold mb-4">
                {selectedAlbum.name}
              </h2>

              <ImageGallery
                items={getGalleryItems(
                  selectedAlbum.familyMediaResponseDtoList || [],
                  selectedAlbum,
                )}
                startIndex={selectedAlbumIndex} // ✅ start from clicked image
                showPlayButton={false}
                showFullscreenButton={false}
                showIndex={true}
              />
            </div>
          </div>
        )}

        {/* Album Add/Edit Modal */}
        {showAlbumModal && (
          <AlbumModal
            mode={modalMode}
            album={currentAlbum}
            familyId={familyId}
            token={token}
            onSave={handleSaveAlbum}
            onClose={() => setShowAlbumModal(false)}
          />
        )}

        {/* Media Modal */}
        {showMediaModal && (
          <MediaModal
            albumId={activeAlbumId}
            token={token}
            onSave={handleAddMedia}
            onClose={() => setShowMediaModal(false)}
            albumCurrentPhotosCount={
                albums.find(a => a.id === activeAlbumId)?.familyMediaResponseDtoList?.length || 0
              }
          />
        )}
      </div>
      <ConfirmModal
        show={confirmDelete.show}
        title="Confirm Delete"
        message="Are you sure you want to delete this? This action cannot be undone."
        onCancel={() =>
          setConfirmDelete({
            show: false,
            type: null,
            albumId: null,
            mediaId: null,
          })
        }
        onConfirm={confirmDeleteAction}
      />
    </>
  );
};
export default PhotoAlbums;

const ConfirmModal = ({ show, title, message, onConfirm, onCancel }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-sm text-center"
      >
        <h2 className="text-lg font-semibold text-gray-800 mb-2">{title}</h2>
        <p className="text-gray-600 text-sm mb-5">{message}</p>

        <div className="flex justify-center gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md"
          >
            Delete
          </button>
        </div>
      </motion.div>
    </div>
  );
};
